import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export interface AssuranceBateau {
  id: string;
  user_id: string;
  bateau_id: string;
  annee: number;
  cout: number;
  compagnie: string | null;
  numero_contrat: string | null;
  date_echeance: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export const useAssurances = (bateauId?: string) => {
  const [assurances, setAssurances] = useState<AssuranceBateau[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchAssurances = async () => {
    if (!user || !bateauId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('assurances_bateaux')
        .select('*')
        .eq('bateau_id', bateauId)
        .order('annee', { ascending: false });

      if (error) {
        console.error('Erreur lors du chargement des assurances:', error);
        setAssurances([]);
      } else {
        setAssurances(data as AssuranceBateau[]);
      }
    } catch (err) {
      console.error('Erreur:', err);
      setAssurances([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssurances();
  }, [user, bateauId]);

  const ajouterAssurance = async (assurance: Omit<AssuranceBateau, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('assurances_bateaux')
        .insert([{ ...assurance, user_id: user.id }])
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de l\'ajout de l\'assurance:', error);
        toast.error('Erreur lors de l\'ajout de l\'assurance');
        return null;
      }

      toast.success('Assurance ajoutée avec succès');
      await fetchAssurances();
      return data;
    } catch (err) {
      console.error('Erreur:', err);
      toast.error('Erreur lors de l\'ajout');
      return null;
    }
  };

  const modifierAssurance = async (id: string, updates: Partial<AssuranceBateau>) => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('assurances_bateaux')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de la modification:', error);
        toast.error('Erreur lors de la modification');
        return null;
      }

      toast.success('Assurance modifiée avec succès');
      await fetchAssurances();
      return data;
    } catch (err) {
      console.error('Erreur:', err);
      toast.error('Erreur lors de la modification');
      return null;
    }
  };

  const supprimerAssurance = async (id: string) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('assurances_bateaux')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erreur lors de la suppression:', error);
        toast.error('Erreur lors de la suppression');
        return false;
      }

      toast.success('Assurance supprimée');
      await fetchAssurances();
      return true;
    } catch (err) {
      console.error('Erreur:', err);
      toast.error('Erreur lors de la suppression');
      return false;
    }
  };

  const getAssuranceParAnnee = (annee: number): AssuranceBateau | undefined => {
    return assurances.find(a => a.annee === annee);
  };

  return {
    assurances,
    loading,
    ajouterAssurance,
    modifierAssurance,
    supprimerAssurance,
    getAssuranceParAnnee,
    refetch: fetchAssurances
  };
};
